// pages/Auth/ForgotPassword.tsx
import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import AuthService from "@/lib/auth"; // Use AuthService instead of direct supabase
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";

// Schema for Step 1: Email
const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

// Schema for Step 2: Code and New Password
const confirmResetSchema = z.object({
  code: z.string().min(6, "Code must be at least 6 characters"),
  password: z.string().min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "One uppercase letter")
    .regex(/[a-z]/, "One lowercase letter")
    .regex(/[0-9]/, "One number")
    .regex(/[^A-Za-z0-9]/, "One special character"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;
type ConfirmResetFormData = z.infer<typeof confirmResetSchema>;

export default function ForgotPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<"request" | "confirm">("request");
  const [email, setEmail] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const stateEmail = location.state?.email || "";

  const requestForm = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: stateEmail },
  });

  // Sync email if state changes
  useEffect(() => {
    if (stateEmail) {
      requestForm.setValue("email", stateEmail);
      setEmail(stateEmail);
    }
  }, [stateEmail, requestForm]);

  const confirmForm = useForm<ConfirmResetFormData>({
    resolver: zodResolver(confirmResetSchema),
    defaultValues: { code: "", password: "", confirmPassword: "" },
  });

  // Restore state from sessionStorage on mount
  useEffect(() => {
    const savedEmail = sessionStorage.getItem('forgot_password_email');
    const savedStep = sessionStorage.getItem('forgot_password_step');

    if (savedStep === 'confirm' && savedEmail) {
      console.log("♻️ Restoring forgot password state from sessionStorage:", savedEmail);
      setEmail(savedEmail);
      setStep('confirm');
    }
  }, []);

  const onRequestSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    try {
      console.log("📧 Sending reset code to:", data.email);
      const { error } = await AuthService.resetPassword(data.email);

      if (error) {
        throw error;
      }

      console.log("✅ Reset code sent successfully, setting step to confirm");

      // Persist state to handle potential reloads
      sessionStorage.setItem('forgot_password_email', data.email);
      sessionStorage.setItem('forgot_password_step', 'confirm');

      setEmail(data.email);
      setStep("confirm");
      toast.success("Reset code sent!", {
        description: "Please check your email for the verification code.",
      });
    } catch (error: any) {
      console.error("❌ Reset request error:", error);
      toast.error(error.message || "Failed to send reset code");
    } finally {
      setIsLoading(false);
    }
  };

  const clearPersistedState = () => {
    sessionStorage.removeItem('forgot_password_email');
    sessionStorage.removeItem('forgot_password_step');
  };

  const onConfirmSubmit = async (data: ConfirmResetFormData) => {
    setIsLoading(true);
    try {
      console.log("🔐 Confirming reset with code...");
      const { error } = await AuthService.confirmResetPassword(email, data.code, data.password);

      if (error) {
        throw error;
      }

      clearPersistedState();
      toast.success("Password reset successful!", {
        description: "You can now login with your new password.",
      });
      navigate("/login");
    } catch (error: any) {
      console.error("❌ Confirm reset error:", error);
      toast.error(error.message || "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  if (step === "confirm") {
    return (
      <div className="bg-[#f7f8fa] font-sans py-10 px-4">
        <div className="max-w-md mx-auto">
          <Card className="w-full p-6 shadow-lg">
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto w-12 h-12 bg-[#1a7a4a]/10 rounded-full flex items-center justify-center mb-4">
              <Lock className="h-6 w-6 text-[#1a7a4a]" />
            </div>
            <h1 className="text-3xl font-extrabold text-[#1a7a4a] tracking-tight">Set New Password</h1>
            <p className="text-muted-foreground">
              Enter the code sent to {email} and your new password.
            </p>
          </CardHeader>
          <CardContent>
            <Form {...confirmForm}>
              <form onSubmit={confirmForm.handleSubmit(onConfirmSubmit)} className="space-y-4">
                {/* 
                  Hidden username field to "catch" browser autofill. 
                  This prevents the browser from putting the email into the 'code' field.
                */}
                <input
                  type="text"
                  name="username"
                  value={email}
                  readOnly
                  style={{ position: 'absolute', opacity: 0, height: 0, width: 0, zIndex: -1 }}
                  autoComplete="username"
                />

                <FormField
                  control={confirmForm.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Verification Code</FormLabel>
                      <FormControl>
                        <Input
                          id="reset-code"
                          placeholder="123456"
                          {...field}
                          value={field.value || ''}
                          disabled={isLoading}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          autoComplete="one-time-code"
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            console.log("Code numeric input:", value);
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={confirmForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Password</FormLabel>
                      <FormControl>
                        <Input
                          id="new-password"
                          type="password"
                          placeholder="••••••••"
                          {...field}
                          disabled={isLoading}
                          autoComplete="new-password"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={confirmForm.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm Password</FormLabel>
                      <FormControl>
                        <Input
                          id="confirm-password"
                          type="password"
                          placeholder="••••••••"
                          {...field}
                          disabled={isLoading}
                          autoComplete="new-password"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full bg-[#1a7a4a] hover:bg-[#15633c] text-white rounded-2xl h-12 shadow-lg shadow-green-100 transition-all hover:scale-[1.02] font-semibold" disabled={isLoading}>
                  {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Reset Password"}
                </Button>
              </form>
            </Form>
          </CardContent>
          <CardFooter>
            <Button variant="link" onClick={() => {
              clearPersistedState();
              setStep("request");
            }} className="mx-auto text-sm text-primary">
              Back to Email Entry
            </Button>
          </CardFooter>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-[#f7f8fa] font-sans py-10 px-4">
      <div className="max-w-md mx-auto">
        <Card className="w-full p-6 shadow-lg">
        <CardHeader className="text-center space-y-2">
          <h1 className="text-3xl font-extrabold text-[#1a7a4a] tracking-tight">
            Forgot Password?
          </h1>
          <p className="text-muted-foreground">
            Enter your email and we'll send you a verification code
          </p>
        </CardHeader>
        <CardContent>
          <Form {...requestForm}>
            <form
              onSubmit={(e) => {
                console.log("📝 Form onSubmit triggered");
                requestForm.handleSubmit(onRequestSubmit)(e);
              }}
              className="space-y-4"
            >
              <FormField
                control={requestForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="you@example.com"
                        type="email"
                        autoComplete="email"
                        {...field}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full bg-[#1a7a4a] hover:bg-[#15633c] text-white rounded-2xl h-12 shadow-lg shadow-green-100 transition-all hover:scale-[1.02] font-semibold"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Reset Code"
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter className="flex flex-col space-y-2">
          <div className="text-center text-sm">
            Remember your password?{" "}
            <Link
              to="/login"
              onClick={clearPersistedState}
              className="text-primary hover:underline"
            >
              Back to Login
            </Link>
          </div>
        </CardFooter>
      </Card>
      </div>
    </div>
  );
}
